<script setup lang="ts">
/**
 * The composer. Ports the playground input's upload handling — validation,
 * base64 encoding, drag-and-drop, per-file errors — and changes two things:
 * it emits canonical `ContentPart[]` instead of the playground's shape, and the
 * model picker it had welded in is a sibling now, so a transport that does not
 * offer a model choice simply has no picker.
 */
import { computed, ref } from 'vue';
import { Button } from '@asabdev/asab-ui';

import {
  DEFAULT_MAX_FILE_SIZE,
  SUPPORTED_AUDIO_TYPES,
  SUPPORTED_IMAGE_TYPES,
  encodeFileToBase64,
  formatFileSize,
  isAudioFile,
  isImageFile,
  validateFile,
} from '../utils/file-encoding';
import type { ChatCapabilities } from '../types/transport';
import type { ContentPart } from '../types/message';

const props = withDefaults(
  defineProps<{
    capabilities: ChatCapabilities;
    busy?: boolean;
    maxFileSize?: number;
    placeholder?: string;
  }>(),
  { maxFileSize: DEFAULT_MAX_FILE_SIZE, placeholder: 'Send a message…' },
);

const emit = defineEmits<{
  submit: [content: ContentPart[]];
  cancel: [];
}>();

const text = ref('');
const files = ref<File[]>([]);
const fileErrors = ref<string[]>([]);
const encoding = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

const canSubmit = computed(
  () => !props.busy && !encoding.value && (text.value.trim().length > 0 || files.value.length > 0),
);

/**
 * Only the kinds the transport will actually carry.
 *
 * Offering a file type the transport drops silently is worse than not offering
 * it: the user attaches it, sends, and never learns why it did not arrive.
 */
const acceptedTypes = computed<readonly string[]>(() => {
  const kinds = props.capabilities.accept;
  return [
    ...(kinds.includes('image') ? SUPPORTED_IMAGE_TYPES : []),
    ...(kinds.includes('audio') ? SUPPORTED_AUDIO_TYPES : []),
  ];
});

function addFiles(incoming: File[]) {
  const errors: string[] = [];
  for (const file of incoming) {
    const result = validateFile(file, acceptedTypes.value, props.maxFileSize);
    if (!result.valid) {
      errors.push(`${file.name}: ${result.error ?? 'unsupported file'}`);
      continue;
    }
    const duplicate = files.value.some((f) => f.name === file.name && f.size === file.size);
    if (!duplicate) files.value.push(file);
  }
  fileErrors.value = errors;
}

function onSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files) addFiles([...target.files]);
  // Reset so re-picking the same file still fires a change event.
  target.value = '';
}

function onDrop(event: DragEvent) {
  if (props.busy || !props.capabilities.multimodalInput) return;
  if (event.dataTransfer?.files) addFiles([...event.dataTransfer.files]);
}

function removeFile(index: number) {
  files.value.splice(index, 1);
}

async function toParts(): Promise<ContentPart[]> {
  const parts: ContentPart[] = [];
  const body = text.value.trim();
  if (body) parts.push({ type: 'text', text: body });

  for (const file of files.value) {
    const data = await encodeFileToBase64(file);
    if (isImageFile(file)) {
      parts.push({ type: 'image', data, mimeType: file.type, filename: file.name });
    } else if (isAudioFile(file)) {
      parts.push({ type: 'audio', data, mimeType: file.type, filename: file.name });
    }
  }
  return parts;
}

async function submit() {
  if (!canSubmit.value) return;
  encoding.value = true;
  try {
    const parts = await toParts();
    if (parts.length === 0) return;
    emit('submit', parts);
    text.value = '';
    files.value = [];
    fileErrors.value = [];
  } catch {
    fileErrors.value = ['Failed to read an attached file.'];
  } finally {
    encoding.value = false;
  }
}

function onKeydown(event: KeyboardEvent) {
  // Enter sends; Shift+Enter writes a newline. Composing (IME) must never send.
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    void submit();
  }
}
</script>

<template>
  <div
    data-testid="chat-input"
    class="border-t border-gray-200 p-3 dark:border-gray-700"
    @dragover.prevent
    @drop.prevent="onDrop"
  >
    <ul v-if="files.length" data-testid="attachments" class="mb-2 flex flex-wrap gap-2">
      <li
        v-for="(file, i) in files"
        :key="`${file.name}-${i}`"
        class="flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800"
      >
        <span class="max-w-[12rem] truncate">{{ file.name }}</span>
        <span class="text-gray-400">{{ formatFileSize(file.size) }}</span>
        <button
          type="button"
          :aria-label="`Remove ${file.name}`"
          class="text-gray-400 hover:text-gray-600"
          @click="removeFile(i)"
        >
          ×
        </button>
      </li>
    </ul>

    <ul v-if="fileErrors.length" data-testid="file-errors" class="mb-2 space-y-0.5">
      <li v-for="error in fileErrors" :key="error" class="text-xs text-red-600 dark:text-red-400">
        {{ error }}
      </li>
    </ul>

    <div class="flex items-end gap-2">
      <slot name="picker" />

      <button
        v-if="capabilities.multimodalInput"
        type="button"
        data-testid="attach-button"
        aria-label="Attach a file"
        class="rounded-lg border border-gray-300 px-2.5 py-2 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
        :disabled="busy"
        @click="fileInput?.click()"
      >
        📎
      </button>
      <input
        v-if="capabilities.multimodalInput"
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        :accept="acceptedTypes.join(',')"
        @change="onSelect"
      />

      <textarea
        v-model="text"
        data-testid="chat-textarea"
        rows="1"
        :placeholder="placeholder"
        :disabled="busy"
        class="max-h-40 flex-1 resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        @keydown="onKeydown"
      />

      <Button
        v-if="busy && capabilities.cancel"
        variant="danger"
        size="sm"
        data-testid="chat-cancel"
        @click="emit('cancel')"
      >
        Stop
      </Button>
      <Button v-else size="sm" data-testid="chat-send" :disabled="!canSubmit" @click="submit">
        Send
      </Button>
    </div>
  </div>
</template>
